/**
 * Per-module print-format registry + admin assignment store.
 *
 * Each printable MODULE (Sales Invoice, Purchase Invoice, POS bill,
 * Restaurant/Dine bill, KOT) can be assigned one of a small set of
 * FORMATS (A4 tax invoice, A4 purchase invoice, 80mm thermal receipt,
 * kitchen KOT slip). Admin picks the format per module on the
 * Settings → Print Formats page; every print path calls
 * `getAssignedFormat(module)` and renders accordingly.
 *
 * Storage: tenant-scoped localStorage envelope (transient settings, not
 * "the database" — allowed per CLAUDE.md rule 1). A backend gap is filed
 * to persist this server-side so it's shared across users/devices.
 */

export type PrintModule =
  | 'salesInvoice'
  | 'purchaseInvoice'
  | 'pos'
  | 'restaurantBill'
  | 'kot';

export type PrintFormatId =
  | 'a4-tax-invoice'
  | 'a4-purchase-invoice'
  | 'thermal-80'
  | 'kot-slip';

export interface PrintFormatMeta {
  id: PrintFormatId;
  label: string;
  paper: 'A4' | '80mm';
}

export const FORMAT_CATALOG: Record<PrintFormatId, PrintFormatMeta> = {
  'a4-tax-invoice':      { id: 'a4-tax-invoice',      label: 'A4 Tax Invoice (GST)',  paper: 'A4' },
  'a4-purchase-invoice': { id: 'a4-purchase-invoice', label: 'A4 Purchase Invoice',   paper: 'A4' },
  'thermal-80':          { id: 'thermal-80',          label: '80mm Thermal Receipt',  paper: '80mm' },
  'kot-slip':            { id: 'kot-slip',            label: 'Kitchen KOT Slip',      paper: '80mm' },
};

/** Per-module: which formats are valid + the sensible default. */
export const MODULE_FORMATS: Record<
  PrintModule,
  { label: string; options: PrintFormatId[]; default: PrintFormatId }
> = {
  salesInvoice:    { label: 'Sales Invoice',    options: ['a4-tax-invoice', 'thermal-80'],      default: 'a4-tax-invoice' },
  purchaseInvoice: { label: 'Purchase Invoice', options: ['a4-purchase-invoice', 'a4-tax-invoice'], default: 'a4-purchase-invoice' },
  pos:             { label: 'POS Bill',         options: ['thermal-80', 'a4-tax-invoice'],      default: 'thermal-80' },
  restaurantBill:  { label: 'Restaurant / Dine-in Bill', options: ['thermal-80', 'a4-tax-invoice'], default: 'thermal-80' },
  kot:             { label: 'Kitchen KOT',      options: ['kot-slip'],                          default: 'kot-slip' },
};

export const PRINT_MODULES: PrintModule[] = [
  'salesInvoice', 'purchaseInvoice', 'pos', 'restaurantBill', 'kot',
];

const LS_KEY = 'kss-print-formats';

type Assignments = Partial<Record<PrintModule, PrintFormatId>>;

function readAll(): Assignments {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as Assignments) : {};
  } catch {
    return {};
  }
}

/** The format assigned to a module, or its sensible default. */
export function getAssignedFormat(module: PrintModule): PrintFormatId {
  const saved = readAll()[module];
  const allowed = MODULE_FORMATS[module].options;
  return saved && allowed.includes(saved) ? saved : MODULE_FORMATS[module].default;
}

/** Persist an admin's format choice for a module. */
export function setAssignedFormat(module: PrintModule, format: PrintFormatId): void {
  const next = { ...readAll(), [module]: format };
  try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* storage full / disabled */ }
}

/** Reset every module to its default. */
export function resetFormats(): void {
  try { localStorage.removeItem(LS_KEY); } catch { /* */ }
}
